import { MedplumClient } from '@medplum/core'
import {
    Bundle,
    Observation,
    DiagnosticReport,
    ServiceRequest,
    Organization,
    Practitioner
} from '@medplum/fhirtypes'
import { useMedplumContext } from '@medplum/react'
import { QueryKey, useQuery, UseQueryOptions } from 'react-query'
import { filterObservations } from 'src/pages/MUIBased/LabResults/utils/filterObservations'

function getNextPageParams(bundle?: Bundle<Observation>) {
    const url = bundle?.link?.find(link => link.relation === 'next')?.url
    if (url) {
        return new URL(url).searchParams
    }
}

export type LabResultResource =
    | Observation
    | DiagnosticReport
    | ServiceRequest
    | Organization
    | Practitioner

function getLabResultDetails(
    medplum: MedplumClient,
    observationId: string,
    pageSize = 1000
) {
    return async () => {
        const params = new URLSearchParams()
        // Filters
        params.append('_id', observationId)
        params.append('_revinclude', 'DiagnosticReport:result')
        params.append('_include:iterate', 'DiagnosticReport:based-on')
        params.append('_include', 'Observation:has-member')
        params.append('_include:iterate', 'ServiceRequest:requester')
        params.append('_include:iterate', 'ServiceRequest:performer')
        params.append('has-member:missing', 'false')
        params.append('status:not', 'cancelled')
        // Select fields
        params.append(
            '_elements',
            'id,issued,effectiveDateTime,status,performer,hasMember,identifier,category,basedOn,result,requester,name,valueQuantity,valueString,referenceRange,interpretation'
        )
        params.append('_count', `${pageSize}`)

        const observations = await medplum.searchResources(
            'Observation',
            params
        )

        let nextPageParams = getNextPageParams(observations?.bundle)
        while (nextPageParams) {
            const nextPageResults = await medplum.searchResources(
                'Observation',
                nextPageParams
            )
            observations.push(...nextPageResults)
            nextPageParams = getNextPageParams(nextPageResults?.bundle)
        }

        const filteredResults = filterObservations(observations)
        return filteredResults
    }
}

export type LabResultsData = {
    observation: Observation
    diagnosticReport?: DiagnosticReport
    serviceRequest?: ServiceRequest
}

export const useGetLabResultDetails = (
    observationId: string,
    options?: Omit<
        UseQueryOptions<
            LabResultResource[],
            unknown,
            LabResultResource[],
            QueryKey
        >,
        'queryFn'
    >
) => {
    const { medplum, loading } = useMedplumContext()

    const { data: resultDetails, isLoading: labResultLoading } = useQuery<
        LabResultResource[]
    >(
        [`get-lab-result-details-${observationId}`],
        getLabResultDetails(medplum, observationId),
        {
            enabled: !!observationId,
            refetchOnWindowFocus: false,
            ...(options ?? {})
        }
    )

    return {
        isLoading: labResultLoading || loading,
        resultDetails: resultDetails || []
    }
}
